/**
 * A small QR encoder — byte mode, error-correction level M.
 *
 * It exists for one job : hand a desktop user a code their phone can scan to
 * open the augmented-reality view. Pulling in a library for that would mean a
 * CDN, which this app deliberately does not use.
 *
 * Only what that job needs is implemented — byte mode, level M, versions 1 to
 * 20, which covers any URL this site produces. The format and version bits are
 * computed with their BCH codes rather than copied from a table, because a
 * mis-transcribed table produces a code that looks perfect and never scans.
 *
 * `tests/browser/test_qr.js` checks the output by decoding it again with the
 * browser's own BarcodeDetector.
 */

/* eslint-disable no-bitwise */

// level M : EC codewords per block, then the block layout
const LEVEL_M = {
	1: [10, 1, 16, 0, 0],
	2: [16, 1, 28, 0, 0],
	3: [26, 1, 44, 0, 0],
	4: [18, 2, 32, 0, 0],
	5: [24, 2, 43, 0, 0],
	6: [16, 4, 27, 0, 0],
	7: [18, 4, 31, 0, 0],
	8: [22, 2, 38, 2, 39],
	9: [22, 3, 36, 2, 37],
	10: [26, 4, 43, 1, 44],
	11: [30, 1, 50, 4, 51],
	12: [22, 6, 36, 2, 37],
	13: [22, 8, 37, 1, 38],
	14: [24, 4, 40, 5, 41],
	15: [24, 5, 41, 5, 42],
	16: [28, 7, 45, 3, 46],
	17: [28, 10, 46, 1, 47],
	18: [26, 9, 43, 4, 44],
	19: [26, 3, 44, 11, 45],
	20: [26, 3, 41, 13, 42],
};

const ALIGNMENT = {
	1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
	7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
	11: [6, 30, 54], 12: [6, 32, 58], 13: [6, 34, 62], 14: [6, 26, 46, 66],
	15: [6, 26, 48, 70], 16: [6, 26, 50, 74], 17: [6, 30, 54, 78],
	18: [6, 30, 56, 82], 19: [6, 30, 58, 86], 20: [6, 34, 62, 90],
};

// ------------------------------------------------------------------ GF(256)
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(function buildTables() {
	let value = 1;
	for (let i = 0; i < 255; i++) {
		EXP[i] = value;
		LOG[value] = i;
		value <<= 1;
		if (value & 0x100) value ^= 0x11d;
	}
	for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

function mul(a, b) {
	return a && b ? EXP[LOG[a] + LOG[b]] : 0;
}

/** Generator polynomial for `count` error-correction codewords. */
function generator(count) {
	let poly = [1];
	for (let i = 0; i < count; i++) {
		const next = new Array(poly.length + 1).fill(0);
		for (let j = 0; j < poly.length; j++) {
			next[j] ^= poly[j];
			next[j + 1] ^= mul(poly[j], EXP[i]);
		}
		poly = next;
	}
	return poly;
}

function remainder(data, count) {
	const poly = generator(count);
	const out = new Array(count).fill(0);
	for (const byte of data) {
		const factor = byte ^ out[0];
		out.shift();
		out.push(0);
		for (let i = 0; i < count; i++) out[i] ^= mul(poly[i + 1], factor);
	}
	return out;
}

// -------------------------------------------------------------------- BCH
function bitLength(value) {
	let bits = 0;
	while (value) {
		bits++;
		value >>= 1;
	}
	return bits;
}

function polyMod(value, generatorBits) {
	const size = bitLength(generatorBits);
	let rest = value;
	while (bitLength(rest) >= size) {
		rest ^= generatorBits << (bitLength(rest) - size);
	}
	return rest;
}

/** 15 bits : two of level, three of mask, ten of BCH, masked with 0x5412. */
function formatBits(maskIndex) {
	const data = (0b00 << 3) | maskIndex;          // 00 = level M
	const code = (data << 10) | polyMod(data << 10, 0b10100110111);
	return code ^ 0b101010000010010;
}

/** 18 bits, only from version 7 up. */
function versionBits(version) {
	return (version << 12) | polyMod(version << 12, 0b1111100100101);
}

// ----------------------------------------------------------------- encoding
function toBytes(text) {
	return Array.from(new TextEncoder().encode(text));
}

function capacity(version) {
	const [ec, g1, d1, g2, d2] = LEVEL_M[version];
	void ec;
	return g1 * d1 + g2 * d2;
}

function countBits(version) {
	return version < 10 ? 8 : 16;
}

function pickVersion(byteCount) {
	for (let version = 1; version <= 20; version++) {
		const header = 4 + countBits(version);
		if (byteCount * 8 + header <= capacity(version) * 8) return version;
	}
	throw new Error("Contenu trop long pour un QR de version 20");
}

function bitStream(bytes, version) {
	const bits = [];
	const push = (value, length) => {
		for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1);
	};
	push(0b0100, 4);                       // byte mode
	push(bytes.length, countBits(version));
	for (const byte of bytes) push(byte, 8);

	const total = capacity(version) * 8;
	for (let i = 0; i < 4 && bits.length < total; i++) bits.push(0);
	while (bits.length % 8) bits.push(0);
	const padding = [0xec, 0x11];
	let index = 0;
	while (bits.length < total) push(padding[index++ % 2], 8);

	const codewords = [];
	for (let i = 0; i < bits.length; i += 8) {
		let byte = 0;
		for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j];
		codewords.push(byte);
	}
	return codewords;
}

/** Split into blocks, add the check bytes, then interleave as the spec says. */
function interleave(codewords, version) {
	const [ecCount, g1, d1, g2, d2] = LEVEL_M[version];
	const blocks = [];
	let offset = 0;
	for (let i = 0; i < g1; i++) {
		blocks.push(codewords.slice(offset, offset + d1));
		offset += d1;
	}
	for (let i = 0; i < g2; i++) {
		blocks.push(codewords.slice(offset, offset + d2));
		offset += d2;
	}
	const checks = blocks.map((block) => remainder(block, ecCount));

	const out = [];
	const longest = Math.max(...blocks.map((b) => b.length));
	for (let i = 0; i < longest; i++) {
		for (const block of blocks) if (i < block.length) out.push(block[i]);
	}
	for (let i = 0; i < ecCount; i++) {
		for (const check of checks) out.push(check[i]);
	}
	return out;
}

// ------------------------------------------------------------------ matrix
function blank(size) {
	return Array.from({ length: size }, () => new Array(size).fill(null));
}

function placeFinder(matrix, row, column) {
	for (let r = -1; r <= 7; r++) {
		for (let c = -1; c <= 7; c++) {
			const y = row + r;
			const x = column + c;
			if (y < 0 || x < 0 || y >= matrix.length || x >= matrix.length) continue;
			const edge = r === -1 || c === -1 || r === 7 || c === 7;
			const ring = r === 0 || c === 0 || r === 6 || c === 6;
			const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
			matrix[y][x] = edge ? 0 : ring || core ? 1 : 0;
		}
	}
}

function reserve(matrix, version) {
	const size = matrix.length;
	placeFinder(matrix, 0, 0);
	placeFinder(matrix, 0, size - 7);
	placeFinder(matrix, size - 7, 0);

	for (let i = 8; i < size - 8; i++) {
		const bit = i % 2 === 0 ? 1 : 0;
		matrix[6][i] = bit;
		matrix[i][6] = bit;
	}

	// An alignment pattern crosses the timing line and takes precedence over it,
	// so it cannot be skipped just because the centre module is already set.
	// Only the three centres that fall inside a finder are left out.
	const coords = ALIGNMENT[version];
	const last = coords[coords.length - 1];
	for (const row of coords) {
		for (const column of coords) {
			const inFinder = (row === 6 && column === 6)
				|| (row === 6 && column === last)
				|| (row === last && column === 6);
			if (inFinder) continue;
			for (let r = -2; r <= 2; r++) {
				for (let c = -2; c <= 2; c++) {
					const ring = Math.max(Math.abs(r), Math.abs(c));
					matrix[row + r][column + c] = ring === 1 ? 0 : 1;
				}
			}
		}
	}

	matrix[size - 8][8] = 1;                  // the always-dark module
	// format areas, filled in later
	for (let i = 0; i < 9; i++) {
		if (matrix[8][i] === null) matrix[8][i] = 2;
		if (matrix[i][8] === null) matrix[i][8] = 2;
	}
	for (let i = 0; i < 8; i++) {
		if (matrix[8][size - 1 - i] === null) matrix[8][size - 1 - i] = 2;
		if (matrix[size - 1 - i][8] === null) matrix[size - 1 - i][8] = 2;
	}
	if (version >= 7) {
		for (let i = 0; i < 6; i++) {
			for (let j = 0; j < 3; j++) {
				matrix[size - 11 + j][i] = 2;
				matrix[i][size - 11 + j] = 2;
			}
		}
	}
	return matrix;
}

function placeData(matrix, codewords) {
	const size = matrix.length;
	let bitIndex = 0;
	const nextBit = () => {
		const byte = codewords[bitIndex >> 3];
		const bit = byte === undefined ? 0 : (byte >> (7 - (bitIndex & 7))) & 1;
		bitIndex++;
		return bit;
	};
	let upward = true;
	for (let right = size - 1; right > 0; right -= 2) {
		if (right === 6) right = 5;                // the vertical timing column
		for (let step = 0; step < size; step++) {
			const row = upward ? size - 1 - step : step;
			for (let lane = 0; lane < 2; lane++) {
				const column = right - lane;
				if (matrix[row][column] !== null) continue;
				matrix[row][column] = nextBit();
			}
		}
		upward = !upward;
	}
	return matrix;
}

const MASKS = [
	(r, c) => (r + c) % 2 === 0,
	(r) => r % 2 === 0,
	(r, c) => c % 3 === 0,
	(r, c) => (r + c) % 3 === 0,
	(r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
	(r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
	(r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
	(r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function penalty(matrix) {
	const size = matrix.length;
	let score = 0;

	const run = (get) => {
		for (let a = 0; a < size; a++) {
			let last = -1;
			let length = 0;
			for (let b = 0; b < size; b++) {
				const value = get(a, b);
				if (value === last) {
					length++;
				} else {
					if (length >= 5) score += 3 + (length - 5);
					last = value;
					length = 1;
				}
			}
			if (length >= 5) score += 3 + (length - 5);
		}
	};
	run((a, b) => matrix[a][b]);
	run((a, b) => matrix[b][a]);

	for (let r = 0; r < size - 1; r++) {
		for (let c = 0; c < size - 1; c++) {
			const v = matrix[r][c];
			if (v === matrix[r][c + 1] && v === matrix[r + 1][c] && v === matrix[r + 1][c + 1]) {
				score += 3;
			}
		}
	}

	const pattern = [1, 0, 1, 1, 1, 0, 1];
	const hasPattern = (get, a, b) => {
		for (let i = 0; i < 7; i++) if (get(a, b + i) !== pattern[i]) return false;
		const before = [b - 4, b - 3, b - 2, b - 1].every(
			(x) => x < 0 || get(a, x) === 0);
		const after = [b + 7, b + 8, b + 9, b + 10].every(
			(x) => x >= size || get(a, x) === 0);
		return before || after;
	};
	for (let a = 0; a < size; a++) {
		for (let b = 0; b + 6 < size; b++) {
			if (hasPattern((y, x) => matrix[y][x], a, b)) score += 40;
			if (hasPattern((y, x) => matrix[x][y], a, b)) score += 40;
		}
	}

	let dark = 0;
	for (const row of matrix) for (const value of row) if (value) dark++;
	const ratio = (dark * 100) / (size * size);
	score += Math.floor(Math.abs(ratio - 50) / 5) * 10;
	return score;
}

function applyFormat(matrix, maskIndex, version) {
	const size = matrix.length;
	const bits = formatBits(maskIndex);
	const read = (i) => (bits >> i) & 1;

	// The two copies are mirror images of each other : the first runs *down
	// column 8* then left along row 8, the second the other way round. Writing
	// them the wrong way round produces a code that looks perfect, has valid
	// data underneath, and never scans — which is exactly what happened.
	for (let i = 0; i <= 5; i++) matrix[i][8] = read(i);
	matrix[7][8] = read(6);
	matrix[8][8] = read(7);
	matrix[8][7] = read(8);
	for (let i = 9; i <= 14; i++) matrix[8][14 - i] = read(i);

	for (let i = 0; i <= 7; i++) matrix[8][size - 1 - i] = read(i);
	for (let i = 8; i <= 14; i++) matrix[size - 15 + i][8] = read(i);
	matrix[size - 8][8] = 1;

	if (version >= 7) {
		const info = versionBits(version);
		for (let i = 0; i < 18; i++) {
			const bit = (info >> i) & 1;
			matrix[Math.floor(i / 3)][size - 11 + (i % 3)] = bit;
			matrix[size - 11 + (i % 3)][Math.floor(i / 3)] = bit;
		}
	}
	return matrix;
}

/**
 * Boolean matrix for `text`, level M, smallest version that fits.
 *
 * `options.mask` forces one of the eight masks instead of scoring them ; the
 * test suite uses it to compare a single mask against a reference encoder.
 */
function encode(text, options = {}) {
	const bytes = toBytes(String(text));
	const version = pickVersion(bytes.length);
	const codewords = interleave(bitStream(bytes, version), version);
	const size = version * 4 + 17;

	const reserved = reserve(blank(size), version);
	const skeleton = reserved.map((row) => row.slice());
	placeData(reserved, codewords);

	let best = null;
	const only = options.mask;
	for (let maskIndex = 0; maskIndex < 8; maskIndex++) {
		if (only !== undefined && only !== null && maskIndex !== only) continue;
		const candidate = reserved.map((row, r) =>
			row.map((value, c) => {
				if (skeleton[r][c] !== null && skeleton[r][c] !== 2) return value;
				if (skeleton[r][c] === 2) return 0;
				return MASKS[maskIndex](r, c) ? value ^ 1 : value;
			})
		);
		applyFormat(candidate, maskIndex, version);
		const score = penalty(candidate);
		if (!best || score < best.score) best = { score, matrix: candidate, maskIndex };
	}
	return best.matrix.map((row) => row.map((value) => !!value));
}

/** The same thing as an SVG string, ready to drop into a dialog. */
function svg(text, options = {}) {
	const matrix = encode(text);
	const quiet = options.quiet === undefined ? 4 : options.quiet;
	const size = matrix.length + quiet * 2;
	const scale = options.scale || 6;
	let path = "";
	for (let r = 0; r < matrix.length; r++) {
		for (let c = 0; c < matrix.length; c++) {
			if (matrix[r][c]) path += `M${c + quiet} ${r + quiet}h1v1h-1z`;
		}
	}
	return `<svg xmlns="http://www.w3.org/2000/svg" width="${size * scale}" height="${size * scale}"
		viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">
		<rect width="${size}" height="${size}" fill="#ffffff"/>
		<path d="${path}" fill="#0f1419"/>
	</svg>`;
}

const RetailQR = { encode, svg };
if (typeof window !== "undefined") window.RetailQR = RetailQR;
if (typeof module !== "undefined") module.exports = RetailQR;
